<template>
  <v-card border rounded="lg">
    <v-card-text>
      <div
        class="d-flex align-center justify-space-between ga-3 pb-3 mb-4 border-b">
        <div class="d-flex align-center ga-2">
          <v-icon color="success" icon="mdi-circle" size="8"></v-icon>
          <span class="text-uppercase font-weight-bold text-grey-darken-1">
            Резолюції
          </span>
        </div>
        <span class="text-disabled">
          Резолюції створюються на стадії «Розгляд» документа
        </span>
      </div>

      <div
        v-if="!resolutionList.length"
        class="resolution-empty d-flex flex-column align-center justify-center text-center mb-4">
        <v-icon
          color="grey-lighten-1"
          icon="mdi-file-document-outline"
          size="56"></v-icon>
        <div class="font-weight-bold text-grey-darken-1 font-size-18 mt-3">
          Резолюції ще не додано
        </div>
        <div class="text-disabled mt-1">
          Створіть першу резолюцію щоб доручити виконання, вказати термін та
          інструкції
        </div>
      </div>

      <v-table v-else class="resolution-table mb-4">
        <thead>
          <tr>
            <th>Завдання</th>
            <th>Текст резолюції</th>
            <th>Виконавець</th>
            <th>Термін</th>
            <th>Автор</th>
            <th>Статус</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="resolution in resolutionList" :key="resolution.id">
            <td>
              <span class="text-deep-purple-accent-3 font-weight-bold">
                #{{ resolution.id }}
              </span>
            </td>
            <td>
              <div class="font-weight-medium">
                {{ resolution.text }}
              </div>
              <div v-if="resolution.comment" class="text-disabled mt-1">
                {{ resolution.comment }}
              </div>
            </td>
            <td>
              <div class="d-flex flex-column ga-2">
                <div
                  v-for="executor in resolution.executors"
                  :key="executor.id"
                  class="d-flex flex-column ga-1">
                  <div class="font-weight-medium">
                    {{ getPersonName(executor.user) || '—' }}
                  </div>
                  <div class="d-flex align-center flex-wrap ga-2">
                    <v-chip
                      color="blue-lighten-5"
                      density="compact"
                      rounded="lg"
                      size="small">
                      <span class="text-blue-darken-3 font-weight-bold">
                        {{ getExecutorTypeLabel(executor.type) }}
                      </span>
                    </v-chip>
                    <span v-if="executor.note" class="text-disabled">
                      {{ executor.note }}
                    </span>
                  </div>
                </div>
              </div>
            </td>
            <td>
              <span
                v-if="resolution.dueDate"
                class="text-amber-darken-4 font-weight-bold">
                {{ new CustomDate(resolution.dueDate).toString() }}
              </span>
              <span v-else class="text-disabled">—</span>
            </td>
            <td>
              <div class="font-weight-medium text-grey-darken-1">
                {{ getPersonName(resolution.author) || '—' }}
              </div>
              <div class="text-disabled">
                {{
                  new CustomDate(resolution.created_at).toString({
                    time: true,
                  })
                }}
              </div>
            </td>
            <td>
              <div class="d-flex flex-column ga-1">
                <v-chip
                  v-for="executor in resolution.executors"
                  :key="executor.id"
                  :color="executor.task ? 'grey-lighten-3' : 'green-lighten-5'"
                  density="compact"
                  rounded="xl"
                  size="small">
                  <div class="d-flex ga-3 align-center">
                    <router-link
                      v-if="executor.task"
                      class="link"
                      :to="{
                        name: 'single-task',
                        params: { id: executor.task.id },
                      }">
                      {{ executor.task?.name }}
                    </router-link>
                    <span
                      :class="
                        executor.task
                          ? 'text-grey-darken-2'
                          : 'text-green-darken-2'
                      "
                      class="font-weight-bold">
                      {{ executor.task?.status?.name || 'Активна' }}
                    </span>
                  </div>
                </v-chip>
              </div>
            </td>
          </tr>
        </tbody>
      </v-table>

      <create-resolution
        :disabled="!canCreateResolution"
        @created="handleCreated">
        <template #activator="{ props, disabled }">
          <v-btn
            v-bind="props"
            :disabled="disabled && false"
            block
            class="resolution-add-button"
            color="success"
            size="large"
            variant="text">
            <v-icon class="mr-2" icon="mdi-plus"></v-icon>
            <span class="font-weight-bold">Додати резолюцію</span>
          </v-btn>
        </template>
      </create-resolution>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { CustomDate, getPersonName } from 'best-modules/utils'
import { useDocument } from '@/pages/document/single/document'
import CreateResolution from '../dialogs/CreateResolution.vue'
import type { DocumentResolutionExecutorType } from '@/utils/types/document'

defineOptions({
  name: 'Resolutions',
})

const { resolutionList, myReview, getMyReview } = useDocument()

const canCreateResolution = computed<boolean>(() => {
  return !!myReview.value
})

const executorTypeLabels: Record<DocumentResolutionExecutorType, string> = {
  execution: 'Виконання',
  familiarization: 'Ознайомлення',
}

const getExecutorTypeLabel = (type: DocumentResolutionExecutorType) => {
  return executorTypeLabels[type] || type
}

const handleCreated = async () => {
  await getMyReview()
}
</script>

<style scoped lang="scss">
.resolution-empty {
  min-height: 235px;
  border: 1px dashed #d9dde8;
  border-radius: 16px;
}

.resolution-add-button {
  border: 1px dashed #8bd9ad;
  border-radius: 14px;
}

.resolution-table {
  border: 1px solid #e1e5ef;
  border-radius: 14px;

  :deep(table) {
    overflow: hidden;
  }

  :deep(th) {
    color: #6d7184;
    font-size: 14px;
    font-weight: 700;
    text-transform: uppercase;
    white-space: nowrap;
  }

  :deep(td) {
    padding-top: 18px !important;
    padding-bottom: 18px !important;
    vertical-align: top;
  }
}
</style>
